import { WorkSafeBcAnatomicalPosition } from "../Constants/WorkSafeBcAnatomicalPosition";
import { WorkSafeBcRegularPractitionerTimeSpan } from "../Constants/WorkSafeBcRegularPractitionerTimeSpan";
import { WorkSafeBcRehabilitationProgram } from "../Constants/WorkSafeBcRehabilitationProgram";
import { WorkSafeBcReturnToWorkTimeSpan } from "../Constants/WorkSafeBcReturnToWorkTimeSpan";
import { WorkSafeBcAddressModel } from "./WorkSafeBcAddressModel";
import { WorkSafeBcPhysicianReportModelMetadata } from "./WorkSafeBcPhysicianReportModelMetadata";

class WorkSafeBcPhysicianReportModel {
    public ServiceDate: Date | null;
    public EmployerName: string = "";
    public EmployerPhoneNumber: string = "";
    public ClaimNumber: string = "";
    public WorkerMiddleInitial: string = "";
    public OperatingLocationAddress: WorkSafeBcAddressModel;
    public FirstTreatmentRendererName: string = "";
    public InjuryOrFirstTreatmentForConditionDate: Date | null;
    public IsWorkerRegularPractitioner: boolean | null;
    public WorkerRegularPractitionerTimeSpan: WorkSafeBcRegularPractitionerTimeSpan | null;
    public PriorProblemsAffectingRecovery: string = "";
    public HasWorkerBeenDisabledFromLastReportOrInjury: boolean | null;
    public WorkerDisabledDate: Date | null;
    public Diagnosis: string = "";

    /**
     * The 5-character numeric code for the area of injury (body part) from the WorkSafeBC subset of CSA codes (80/80 list).
     */
    public AreaOfInjuryCode: string = "";

    /**
     * The anatomical position code for the area of injury.
     */
    public AnatomicalPosition: WorkSafeBcAnatomicalPosition | null;

    /**
     * The 5-character numeric code for the nature of injury from the WorkSafeBC subset of CSA NOI codes (80/80 list).
     */
    public NatureOfInjuryCode: string = "";

    /**
     * The ICD-9 diagnosis code.
     */
    public DiagnosticCode: string = "";

    public ClinicalInformation: string = "";
    public IsWorkerMedicallyCapableToReturnToWork: boolean | null;
    public CurrentPhysicalOrPsychologicalRestrictions: string = "";
    public DurationOfWorkReturn: WorkSafeBcReturnToWorkTimeSpan | null;
    public IsWorkerReadyForRehab: boolean | null;
    public RehabilitationProgram: WorkSafeBcRehabilitationProgram | null;
    public ConsultWorkSafeBcPhysicianOrNurseAdvisor: boolean | null;
    public EstimatedDateOfMedicalRecovery: Date | null;
    public PayeeNumber: string = "";
    public PayeeName: string = "";
    public PractitionerNumber: string = "";
    public PractitionerName: string = "";
    public AdditionalInformation: string = "";

    public AreaOfInjuryCodeDescription: string = "";
    public NatureOfInjuryCodeDescription: string = "";
    public DiagnosticCodeDescription: string = "";

    public Metadata: WorkSafeBcPhysicianReportModelMetadata;

    public constructor(init?: Partial<WorkSafeBcPhysicianReportModel>) {
        Object.assign(this, init);
        this.OperatingLocationAddress = new WorkSafeBcAddressModel(init?.OperatingLocationAddress);
        this.Metadata = new WorkSafeBcPhysicianReportModelMetadata(init?.Metadata);
    }

    public getAnatomicalPositionString(): string {
        switch (this.AnatomicalPosition) {
            case WorkSafeBcAnatomicalPosition.Left:
                return cacheStore.GetString("WorkSafeBc_AnatomicalPositionOption_Left");
            case WorkSafeBcAnatomicalPosition.Right:
                return cacheStore.GetString("WorkSafeBc_AnatomicalPositionOption_Right");
            case WorkSafeBcAnatomicalPosition.LeftAndRight:
                return cacheStore.GetString("WorkSafeBc_AnatomicalPositionOption_LeftAndRight");
            case WorkSafeBcAnatomicalPosition.NotApplicable:
                return cacheStore.GetString("WorkSafeBc_AnatomicalPositionOption_NotApplicable");
            default:
                return "";
        }
    }

    public setAnatomicalPositionFromString(positionString: string): void {
        switch (positionString) {
            case WorkSafeBcAnatomicalPosition.Left.toString():
                this.AnatomicalPosition = WorkSafeBcAnatomicalPosition.Left;
                break;
            case WorkSafeBcAnatomicalPosition.Right.toString():
                this.AnatomicalPosition = WorkSafeBcAnatomicalPosition.Right;
                break;
            case WorkSafeBcAnatomicalPosition.LeftAndRight.toString():
                this.AnatomicalPosition = WorkSafeBcAnatomicalPosition.LeftAndRight;
                break;
            case WorkSafeBcAnatomicalPosition.NotApplicable.toString():
                this.AnatomicalPosition = WorkSafeBcAnatomicalPosition.NotApplicable;
                break;
            default:
                this.AnatomicalPosition = null;
        }
    }

    public getAreaOfInjuryAndPositionString(): string {
        return this.getAreaOfInjuryCodeString()
            + (this.AnatomicalPosition ? ` / ${this.getAnatomicalPositionString()}` : "");
    }

    public getAreaOfInjuryCodeString(): string {
        return this.AreaOfInjuryCodeDescription?.trim() || this.AreaOfInjuryCode || "";
    }

    public getNatureOfInjuryCodeString(): string {
        return this.NatureOfInjuryCodeDescription?.trim() || this.NatureOfInjuryCode || "";
    }

    public getDiagnosticCodeString(): string {
        return this.DiagnosticCodeDescription?.trim() || this.DiagnosticCode || "";
    }
}

export { WorkSafeBcPhysicianReportModel };
